import React, { useState, useEffect, useMemo } from 'react';
import { useAnalyticsUtility } from '../../hooks/useAnalyticsUtility';
import {
  Gauge,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  Wifi,
  Cpu,
  MemoryStick,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Settings,
  RefreshCw
} from 'lucide-react';

interface PerformanceMetric {
  name: string;
  value: number;
  threshold: { warning: number; critical: number };
  unit: string;
  status: 'healthy' | 'warning' | 'critical';
  trend: 'up' | 'down' | 'stable';
  icon: React.ReactNode;
}

interface SystemAlert {
  id: string;
  type: 'warning' | 'error' | 'info';
  title: string;
  message: string;
  timestamp: string;
  acknowledged: boolean;
}

export const PerformanceMonitor: React.FC = () => {
  const { performanceMetrics, isLoading, filters } = useAnalyticsUtility();
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000);

  // Generate performance metrics
  const performanceMetricsData = useMemo(() => {
    const metrics: PerformanceMetric[] = [
      {
        name: 'Data Load Time',
        value: performanceMetrics.loadTime,
        threshold: { warning: 2000, critical: 5000 },
        unit: 'ms',
        status: performanceMetrics.loadTime > 5000 ? 'critical' : performanceMetrics.loadTime > 2000 ? 'warning' : 'healthy',
        trend: 'down',
        icon: <Clock className="h-4 w-4" />
      },
      {
        name: 'Chart Render Time',
        value: performanceMetrics.renderTime,
        threshold: { warning: 1000, critical: 3000 },
        unit: 'ms',
        status: performanceMetrics.renderTime > 3000 ? 'critical' : performanceMetrics.renderTime > 1000 ? 'warning' : 'healthy',
        trend: 'down',
        icon: <Cpu className="h-4 w-4" />
      },
      {
        name: 'Data Points',
        value: performanceMetrics.dataPoints,
        threshold: { warning: 100, critical: 500 },
        unit: 'points',
        status: performanceMetrics.dataPoints > 500 ? 'critical' : performanceMetrics.dataPoints > 100 ? 'warning' : 'healthy',
        trend: 'up',
        icon: <Database className="h-4 w-4" />
      },
      {
        name: 'API Response Time',
        value: Math.random() * 500 + 100,
        threshold: { warning: 1000, critical: 2000 },
        unit: 'ms',
        status: 'healthy',
        trend: 'stable',
        icon: <Wifi className="h-4 w-4" />
      },
      {
        name: 'Memory Usage',
        value: Math.random() * 30 + 20,
        threshold: { warning: 80, critical: 95 },
        unit: '%',
        status: 'healthy',
        trend: 'stable',
        icon: <MemoryStick className="h-4 w-4" />
      }
    ];

    return metrics;
  }, [performanceMetrics]);

  // Generate alerts based on performance metrics
  useEffect(() => {
    const newAlerts: SystemAlert[] = [];

    performanceMetricsData.forEach(metric => {
      if (metric.status === 'critical') {
        newAlerts.push({
          id: `${metric.name}-critical-${Date.now()}`,
          type: 'error',
          title: `${metric.name} Critical`,
          message: `${metric.name} is at ${metric.value.toFixed(2)}${metric.unit}, which exceeds the critical threshold.`,
          timestamp: new Date().toISOString(),
          acknowledged: false
        });
      } else if (metric.status === 'warning') {
        newAlerts.push({
          id: `${metric.name}-warning-${Date.now()}`,
          type: 'warning',
          title: `${metric.name} Warning`,
          message: `${metric.name} is at ${metric.value.toFixed(2)}${metric.unit}, approaching the warning threshold.`,
          timestamp: new Date().toISOString(),
          acknowledged: false
        });
      }
    });

    setAlerts(prev => [...newAlerts, ...prev].slice(0, 10));
  }, [performanceMetricsData, isLoading]);

  const acknowledgeAlert = React.useCallback((alertId: string) => {
    setAlerts(prev => prev.map(alert =>
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  }, []);

  const clearAlerts = React.useCallback(() => {
    setAlerts([]);
  }, []);

  const getMetricColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getMetricBgColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 dark:bg-green-900/20';
      case 'warning': return 'bg-yellow-100 dark:bg-yellow-900/20';
      case 'critical': return 'bg-red-100 dark:bg-red-900/20';
      default: return 'bg-gray-100 dark:bg-gray-800/50';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'stable': return <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Performance Overview */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Performance Monitor</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Auto Refresh</span>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''} text-gray-500 dark:text-gray-400`} />
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={!autoRefresh}
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary text-sm"
                aria-label="Refresh interval"
                title="Set refresh interval"
              >
                <option value={10000}>10s</option>
                <option value={30000}>30s</option>
                <option value={60000}>1m</option>
                <option value={300000}>5m</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {performanceMetricsData.map(metric => (
            <div key={metric.name} className={`border-l-4 rounded-lg p-4 ${getMetricBgColor(metric.status)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getMetricColor(metric.status)}`}>
                    {metric.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-dark-text-primary">{metric.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{metric.value.toFixed(2)} {metric.unit}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getTrendIcon(metric.trend)}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${metric.status === 'healthy' ? 'bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                    metric.status === 'warning' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                    {metric.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${metric.status === 'healthy' ? 'bg-green-500' :
                    metric.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                  style={{
                    width: `${Math.min((metric.value / metric.threshold.critical) * 100, 100)}%`
                  }}
                ></div>
              </div>

              <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Warning: {metric.threshold.warning}{metric.unit}</span>
                <span>Critical: {metric.threshold.critical}{metric.unit}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Alerts */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">System Alerts</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={clearAlerts}
              className="px-3 py-2 text-sm text-gray-600 bg-gray-100 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Clear All
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {alerts.filter(a => !a.acknowledged).length} unread
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No alerts at this time. System is running smoothly!
            </div>
          ) : (
            alerts.map(alert => (
              <div
                key={alert.id}
                className={`border-l-4 rounded-lg p-4 ${alert.type === 'error' ? 'bg-red-50 dark:bg-red-900/10 border-red-400' :
                  alert.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-400' :
                    'bg-blue-50 dark:bg-blue-900/10 border-blue-400'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${alert.type === 'error' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                      alert.type === 'warning' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                      {alert.type === 'error' ? (
                        <AlertTriangle className="h-4 w-4" />
                      ) : alert.type === 'warning' ? (
                        <AlertTriangle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-dark-text-primary">{alert.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{alert.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(alert.timestamp).toLocaleTimeString()}
                    </span>
                    {!alert.acknowledged && (
                      <button
                        onClick={() => acknowledgeAlert(alert.id)}
                        className="px-3 py-1 text-xs bg-white border border-gray-300 dark:bg-dark-bg-primary dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        Acknowledge
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Performance Tips */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Performance Tips</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Data Optimization</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Reduce date range for faster loading</li>
              <li>• Use specific town filters when possible</li>
              <li>• Limit chart data points for better performance</li>
              <li>• Enable real-time updates only when needed</li>
            </ul>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 dark:text-green-300 mb-2">Chart Performance</h4>
            <ul className="text-sm text-green-800 dark:text-green-200 space-y-1">
              <li>• Use appropriate chart types for your data</li>
              <li>• Disable zoom/pan for simple charts</li>
              <li>• Reduce chart refresh frequency</li>
              <li>• Close drill-down panels when not in use</li>
            </ul>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 dark:text-purple-300 mb-2">Mobile Optimization</h4>
            <ul className="text-sm text-purple-800 dark:text-purple-200 space-y-1">
              <li>• Use simplified charts on mobile devices</li>
              <li>• Increase touch target sizes for interactions</li>
              <li>• Optimize image exports for mobile screens</li>
              <li>• Consider offline caching for frequently accessed data</li>
            </ul>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <h4 className="font-semibold text-orange-900 dark:text-orange-300 mb-2">System Monitoring</h4>
            <ul className="text-sm text-orange-800 dark:text-orange-200 space-y-1">
              <li>• Monitor load times regularly</li>
              <li>• Set up alerts for critical thresholds</li>
              <li>• Review performance metrics weekly</li>
              <li>• Clear browser cache if issues persist</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};